import React, { useState, useMemo } from 'react';
import { RiCloseLine, RiPlaneLine, RiCalendar2Line, RiTeamLine, RiMapPinLine, RiErrorWarningLine, RiRefreshLine, RiBuildingLine, RiPriceTag3Line, RiCheckLine, RiTrophyLine, RiMedalLine, RiLoader4Line, RiDownload2Line, RiFileLine, RiTimeLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiCalendarLine, RiCalendarScheduleLine, RiFileCopyLine, RiPencilLine, RiBankCardLine, RiBankLine, RiCloseCircleFill, RiNoCreditCardLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/Drawer';
import { Skeleton } from '@components/ui/Skeleton';
import Modal from '@components/ui/Modal';
import { TrustScore } from '../../trust-score';
import { apiClient } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import ImageCarousel from '@components/ui/ImageCarousel';
import { buildFlightEmailHtml } from '@/utils/emailTemplates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { patchQuoteFiles } from '../services/quoteService';
import { formatPrice, trunc2 } from '@/utils';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos, getEffectiveAircraft } from '@/features/tails/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { getQuoteStatusConfig, getEffectiveQuoteStatus, selectAcceptedBidOption, selectPrimaryBidOption } from '../utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';
import { useQuoteDuplicator } from '../hooks/useQuoteDuplicator';
import { resolveAssetUrl } from '@/utils/urls';
import { storageKeys } from '@/lib/storage';
import PaymentSettlementNoticeModal, { isSettlementNoticeDismissed } from './PaymentSettlementNoticeModal';
import Swal from 'sweetalert2';
import { buildQuotePaymentHoldEmailHtml, buildQuotePaymentSecuredBookingEmailHtml } from '@/utils/emailTemplates';

/**
 * Format a user record's first/last name into a single display string.
 * Returns "—" when the record is missing or has no name — used for the
 * admin-only Created By / Sent By rows so a not-yet-sent quote shows a
 * neutral dash instead of an empty gap.
 */
const formatUserFullName = (user) => {
  if (!user) return '—';
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full || user.email || '—';
};

/**
 * Fetch a remote file (contract PDF, etc.) and return its base64 payload,
 * stripped of the `data:...;base64,` prefix so it can drop straight into
 * an email API's `Content` field. Throws on any network / decode error;
 * callers decide whether to fall back or bubble.
 */
const fetchUrlAsBase64 = async (url) => {
  const resp = await fetch(url, { cache: 'no-store' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = String(reader.result || '');
      const base64 = dataUrl.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * QuoteDetailsDrawer Component
 * Displays detailed quote information in a drawer
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.onOpenChange - Callback when drawer open state changes
 * @param {string} props.quoteId - ID of the quote to display
 */
const QuoteDetailsDrawer = ({ isOpen, onOpenChange, quoteId, status, paymentStatus }) => {

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSelectedBidOptions } = useAppStore();
  const { userProfile } = useAuthStore();

  // "Build Trip Sheet" requires full CRUD on Booking & Quoting
  // (admin + broker only — junior_broker is draft-only, read-only roles excluded).
  const { can, role } = usePermissions();
  // flight_coordinator, finance, and compliance_qa shouldn't see Duplicate /
  // Edit actions on a quote — they're observers, not editors.
  const normalizedRole = normalizeRole(role);
  const isQuoteViewerRole =
    normalizedRole === ROLES.FLIGHT_COORDINATOR ||
    normalizedRole === ROLES.FINANCE ||
    normalizedRole === ROLES.COMPLIANCE_QA;
  const isFinanceRole = normalizedRole === ROLES.FINANCE;
  // Admin-only fields (Created By / Sent By) — gates the audit footer at
  // the bottom of the drawer. super_admin sees admin-only surfaces by
  // convention across this codebase.
  const isAdminRole =
    normalizedRole === ROLES.ADMIN || normalizedRole === ROLES.SUPER_ADMIN;
  const canBuildTripSheet =
    can(MODULES.BOOKING, ACTIONS.CREATE) &&
    can(MODULES.BOOKING, ACTIONS.UPDATE) &&
    can(MODULES.BOOKING, ACTIONS.DELETE);

  const [isSending, setIsSending] = useState(false);
  const [flightActionLoading, setFlightActionLoading] = useState(null);
  const [showFlightPicker, setShowFlightPicker] = useState(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineConfirmOpen, setDeclineConfirmOpen] = useState(false);
  const [authActionLoading, setAuthActionLoading] = useState(null); // 'confirmCapture' | 'confirmOnly' | 'reject'

  // Themed-modal state for the duplicate flow (replaces window.confirm).
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [editPromptOpen, setEditPromptOpen] = useState(false);
  // Holds the newly-created duplicate so the "edit now?" dialog can navigate to it.
  const [duplicatedQuote, setDuplicatedQuote] = useState(null);
  // Payment Settlement Notice — one-time-per-user informational modal shown
  // before capturing card funds. Users can opt out via "Don't show again",
  // which persists via localStorage so subsequent captures skip straight to
  // handleConfirmAndCapture.
  const [settlementNoticeOpen, setSettlementNoticeOpen] = useState(false);
  // Card capture failure — the toast shows the human summary; the modal
  // holds the raw provider error, revealed only when the broker clicks
  // "Learn more" from the toast.
  const [captureErrorOpen, setCaptureErrorOpen] = useState(false);
  const [captureErrorDetails, setCaptureErrorDetails] = useState('');

  const openConfirmAndCaptureFlow = () => {
    // The settlement disclaimer only applies to pre-auth card captures —
    // that's when the 1–3 day bank-settlement window (and its decline risk)
    // is actually in play. Any other payment state skips the notice and
    // goes straight to the action. Broker's per-user opt-out still wins.
    const dismissed = isSettlementNoticeDismissed(
      storageKeys.QUOTE_SETTLEMENT_NOTICE_DISMISSED,
      userProfile?.userprofile_id,
    );
    if (dismissed) {
      handleConfirmAndCapture();
      return;
    }
    setSettlementNoticeOpen(true);
  };

  const { duplicateQuote, isDuplicating, currentStep: duplicateStep } = useQuoteDuplicator();

  // Fetch quote details using React Query
  const { data: quote, isLoading: loading, error, refetch: loadQuoteDetails } = useQuery({
    queryKey: ['quoteDetails', quoteId],
    queryFn: async () => {
      console.log('QuoteDetailsDrawer: Fetching quote:', quoteId);
      const data = await apiClient.getQuoteById(quoteId);
      console.log('QuoteDetailsDrawer: Got quote data:', data);
      return data;
    },
    enabled: isOpen && !!quoteId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  // The bid option the client accepted at checkout — used to drive
  // post-payment views (booking summary, auth actions, invoices).
  // Prefer status='ACCEPTED'; if none is flagged (early lifecycle or
  // API hasn't hydrated the status yet), fall back to the shortlisted
  // one, then to the first bid option available.
  const bidOption = useMemo(() => {
    if (!quote) return null;
    const allOptions = (quote.bid_items || []).flatMap(
      (bidItem) => bidItem.bid?.bid_options || []
    );
    return selectPrimaryBidOption(allOptions);
  }, [quote]);

  // 7-day pre-auth window (Visa/MC standard). Past that window the funds
  // are released automatically and any sale-completion call will fail.
  const holdAnchor =
    quote?.invoice?.created_at
    || quote?.payments?.created_at;
  const holdExpiresAt = holdAnchor
    ? new Date(new Date(holdAnchor).getTime() + 7 * 24 * 60 * 60 * 1000)
    : null;
  const isHoldExpired = holdExpiresAt && Date.now() > holdExpiresAt.getTime();

  const { data: brokerProfile } = useQuery({
    queryKey: ['broker-profile', quote?.created_by],
    // quote.created_by holds a userprofile_id — hit /users/{userprofile_id}
    // (getUser), NOT the by-user-id variant which resolves a Supabase
    // auth user id and 404s on userprofile ids.
    queryFn: () => apiClient.getUser(quote.created_by),
    enabled: !!quote?.created_by
  });

  const { data: organization } = useQuery({
    queryKey: ['organization', quote?.broker_org_id],
    queryFn: () => apiClient.getOrganizationById(quote.broker_org_id),
    enabled: !!quote?.broker_org_id,
  });

  const { data: flightData } = useQuery({
    queryKey: ['flightByQuote', quoteId],
    queryFn: () => apiClient.getFlights({ quote_id: quoteId, include: 'aircrafts' }),
    enabled: isOpen && !!quoteId,
  });

  const handleFlightStatusUpdate = async (status, flightId) => {
    const flights = Array.isArray(flightData) ? flightData : [];
    if (!flightId && flights.length > 1) {
      setShowFlightPicker(status.toLowerCase());
      return;
    }
    const targetId = flightId || flights[0]?.flight_id;
    if (!targetId) return;
    const loadingKey = `${status.toLowerCase()}-${targetId}`;
    setFlightActionLoading(loadingKey);
    try {
      await apiClient.patchFlight(targetId, { flight_status: status });
      queryClient.invalidateQueries(['quoteDetails', quoteId]);
      queryClient.invalidateQueries(['flightByQuote', quoteId]);
      
      // Generate tracking URLs for each flight
      const tokenResponse = await apiClient.createTrackingToken(quoteId, quote.bid_items?.[0]?.bid?.leg_itinerary?.length || '1');

      const trackingUrl = {
        flightId: targetId,
        route: `${flights[0]?.departure_airport || '—'} → ${flights[0]?.arrival_airport || '—'}`,
        url: `${window.location.origin}/track/${targetId}?token=${tokenResponse.tracking_token}`,
      };

      // Send flight tracking email to client
      if (quote?.client?.email && trackingUrl) {
        const emailHtml = await buildFlightEmailHtml({
          quote,
          trackingUrl: trackingUrl,
          flights,
          organization,
        });

        const emailPayload = {
          From: "tools@trustjet.ai",
          To: quote.client.email,
          Subject: `Your Flight is Ready – Track Your ${quote.departure_airport} to ${quote.arrival_airport} Trip`,
          HtmlBody: emailHtml,
        };

        apiClient.sendMessage(emailPayload)
          .then(() => console.log('Flight tracking email sent to', quote.client.email))
          .catch((err) => console.error('Failed to send flight tracking email:', err));
      }
      
      
    } catch (e) {
      console.error(`Failed to set ${status}:`, e);
    } finally {
      setFlightActionLoading(null);
    }
  };

  // Clicking the Duplicate icon opens the themed confirm modal.
  const handleDuplicateQuote = () => {
    if (!quote || isDuplicating) return;
    setDuplicateConfirmOpen(true);
  };

  // Runs the duplication after the user confirms in the themed modal.
  // The modal stays open until the API work finishes so the broker sees the
  // spinner + step label on the Duplicate button — closing it pre-emptively
  // would make the action look instantaneous (or worse, ignored).
  const runDuplicate = async () => {
    if (!quote || isDuplicating) return;

    const result = await duplicateQuote(quote);
    setDuplicateConfirmOpen(false);
    if (result.success && result.data?.quote_id) {
      toast.success(`Duplicate created — ${result.data.quote_number || 'new draft'}`);
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      // Stash the duplicate so the "edit now?" modal can navigate to it.
      setDuplicatedQuote(result.data);
      setEditPromptOpen(true);
    }
  };

  // "Edit now?" modal — confirm jumps to /quotes/generate?quoteid=…
  const acceptEditDuplicate = () => {
    const id = duplicatedQuote?.quote_id;
    setEditPromptOpen(false);
    onOpenChange?.(false);
    if (id) navigate(`/quotes/generate?quoteid=${id}`);
  };

  const dismissEditDuplicate = () => {
    setEditPromptOpen(false);
    setDuplicatedQuote(null);
    onOpenChange?.(false);
  };

  const handleMarkAsPaid = async () => {
    if (isMarkingPaid || !quoteId) return;
    setIsMarkingPaid(true);
    try {
      await apiClient.updateQuote(quoteId, { status: 'ACCEPTED' });
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Quote marked as Paid');
    } catch (e) {
      console.error('Failed to mark quote as paid:', e);
      toast.error('Failed to mark quote as Paid');
    } finally {
      setIsMarkingPaid(false);
    }
  };

  // Admin-only Decline on a PENDING_APPROVAL quote. Terminal transition:
  // the JB has to duplicate and resubmit if they want to try again.
  // Also stamps a `decline` audit onto quote.files so we know who declined
  // and when. Merge-safe patch preserves siblings (tripsheet URL, contract,
  // crews, additional_services, fbo, etc.).
  const handleDecline = async () => {
    if (isDeclining || !quoteId) return;
    setIsDeclining(true);
    try {
      await patchQuoteFiles(
        quoteId,
        {
          decline: {
            declined_by: userProfile?.userprofile_id,
            declined_at: new Date().toISOString(),
          },
        },
        { status: 'DECLINED' },
      );
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Quote declined');
      setDeclineConfirmOpen(false);
      onOpenChange?.(false);
    } catch (e) {
      console.error('Failed to decline quote:', e);
      toast.error('Failed to decline quote');
    } finally {
      setIsDeclining(false);
    }
  };
  
  const handleConfirmAndCapture = async () => {

    if (authActionLoading || !quoteId) return;
    if (isHoldExpired) {
      toast.error('Payment hold has expired. Re-authorization is required before this trip can be captured.');
      return;
    }
    setAuthActionLoading('confirmCapture');
    try {

      // capture sales
      const sales = await apiClient.saleCompletionMxMerchantPayment({ quote_id: quoteId });

      if(sales?.status=="CAPTURED")
      {
        await apiClient.updateQuote(quoteId, { status: 'ACCEPTED' });

        // Create a PENDING flight record per leg on the accepted
        // itinerary. Best-effort: a failure here logs but doesn't block
        // the capture flow (email + settlement notice still run below).
        try {
          const legs = quote?.bid_items?.[0]?.bid?.leg_itinerary || [];
          const allOptions = (quote?.bid_items || []).flatMap(
            (bi) => bi?.bid?.bid_options || []
          );
          const acceptedOption = selectAcceptedBidOption(allOptions);
          const aircraftId = acceptedOption?.aircraft_id;
          if (legs.length && aircraftId) {
            await apiClient.createFlightsBatch(
              legs.map((f) => ({
                quote_id: quoteId,
                aircraft_id: aircraftId,
                flight_status: 'PENDING',
                departure_airport: f.departure_airport,
                arrival_airport: f.arrival_airport,
                departure_date: f.departure_date,
                arrival_date: null,
              }))
            );
          }
        } catch (flightsErr) {
          console.error('Failed to create flight records after capture:', flightsErr);
        }
      }

      // Prefer the persisted signed contract on quote.files.contract_signed
      // (fetched → base64). Fall back to sessionStorage for the case where
      // the client signed in the current session but the URL hasn't
      // hydrated on the quote yet. Any failure at any step (bad URL,
      // network, CORS, disabled storage, decode error) silently drops the
      // attachment — the capture flow continues and the email still
      // sends without the PDF.
      let contractAttachment = [];
      try {
        const contractPdfFileName = sessionStorage.getItem('contractPdfFileName') || 'Charter_Contract.pdf';
        let contractPdfBase64 = null;
        const contractSignedUrl = quote?.files?.contract_signed;
        if (contractSignedUrl) {
          try {
            contractPdfBase64 = await fetchUrlAsBase64(resolveAssetUrl(contractSignedUrl));
          } catch (err) {
            console.error('Failed to fetch signed contract from URL, falling back to sessionStorage:', err);
          }
        }
        if (!contractPdfBase64) {
          contractPdfBase64 = sessionStorage.getItem('contractPdfBase64');
        }
        if (contractPdfBase64) {
          contractAttachment = [{ Name: contractPdfFileName, Content: contractPdfBase64, ContentType: 'application/pdf' }];
        }
      } catch (attachmentErr) {
        console.error('Failed to prepare signed contract attachment, sending email without it:', attachmentErr);
      }

      // Send payment confirmation email to client
      if (quote?.client?.email) {
        try {

          const emailHtml = await buildQuotePaymentSecuredBookingEmailHtml({
            quote,
            selectedBidOption: bidOption,
            client: quote?.client,
            sales,
            organization
          });

          apiClient.sendMessage({
            From: 'tools@trustjet.ai',
            To: quote.client?.email,
            ReplyTo: brokerProfile?.email || '',
            Subject: `TrustJet – Booking Secured (${quote?.quote_number})`,
            HtmlBody: emailHtml,
            ...(contractAttachment.length > 0 && { Attachments: contractAttachment }),
          })
            .then(() => console.log('Payment confirmation email sent to', quote.client.email))
            .catch((err) => console.error('Failed to send payment confirmation email:', err));
        } catch (emailErr) {
          console.error('Failed to build payment confirmation email:', emailErr);
        }
      }

      // Send payment confirmation to organization/broker
      if (brokerProfile?.email) {
        // try {
        //   const orgEmailHtml = await buildPaymentConfirmationEmailHtml({
        //     quote,
        //     selectedBidOption: bidOption,
        //     paymentResult: { transactionId: sales?.external_transaction_id, reference: sales?.related_transactions?.sale_completion?.reference, totalAmount: sales?.amount, amount: sales?.amount, paymentMethod: sales?.related_transactions?.sale_completion?.cardAccount },
        //     bookingNumber: sales?.related_transactions?.sale_completion?.reference || '',
        //     isBroker: true,
        //   });

        //   apiClient.sendMessage({
        //     From: 'tools@trustjet.ai',
        //     To: brokerProfile?.email,
        //     Subject: `TrustJet – Trip Paid by Client (${quoteId})`,
        //     HtmlBody: orgEmailHtml,
        //     ...(contractAttachment.length > 0 && { Attachments: contractAttachment }),
        //   })
        //     .then(() => console.log('Payment confirmation email sent to org:', brokerProfile?.email))
        //     .catch((err) => console.error('Failed to send org payment confirmation email:', err));
        // } catch (emailErr) {
        //   console.error('Failed to build org payment confirmation email:', emailErr);
        // }
      }

      //invalidates quotes
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      //success toast message
      toast.success('Trip confirmed and payment is being processed.');

      // close drawer
      onOpenChange?.(false);
    } catch (e) {
      console.error('Failed to confirm & capture:', e);
      const tripNumber = quote?.quote_number || quoteId;
      const details = e?.message || 'Unknown error';
      setCaptureErrorDetails(details);
      // Custom JSX so "Learn more" renders as a red underlined link
      // stacked below the message. Hit-testing is reliable because the
      // Toaster in App.jsx explicitly sets pointer-events:auto to
      // override Radix Dialog's body lockout.
      toast.error(
        <div className="flex items-start gap-2">
          <RiCloseCircleFill className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <div className="flex flex-col items-start gap-1">
            <span>Card payment for <span className="font-semibold">{tripNumber}</span> failed to clear.</span>
            <button
              type="button"
              onClick={() => setCaptureErrorOpen(true)}
              className="cursor-pointer text-red-600 underline underline-offset-2 hover:text-red-700 text-sm"
            >
              Learn more
            </button>
          </div>
        </div>,
        { duration: 10000, icon: null },
      );
    } finally {
      setAuthActionLoading(null);
    }
  };

  const handleConfirmWithoutCapture = async () => {
   
    if (authActionLoading || !quoteId) return;
    if (isHoldExpired) {
      toast.error('Payment hold has expired. Re-authorization is required before this trip can be captured.');
      return;
    }
    setAuthActionLoading('confirmOnly');
    try {

      // update quote to confirmed and paid
      await apiClient.updateQuote(quoteId, { status: 'ACCEPTED', payment_status: 'CAPTURED' });

      // Create a PENDING flight record per leg on the accepted
      // itinerary. Best-effort: a failure here logs but doesn't block
      // the capture flow (email + settlement notice still run below).
      try {
        const legs = quote?.bid_items?.[0]?.bid?.leg_itinerary || [];
        const allOptions = (quote?.bid_items || []).flatMap(
          (bi) => bi?.bid?.bid_options || []
        );
        const acceptedOption = selectAcceptedBidOption(allOptions);
        const aircraftId = acceptedOption?.aircraft_id;
        if (legs.length && aircraftId) {
          await apiClient.createFlightsBatch(
            legs.map((f) => ({
              quote_id: quoteId,
              aircraft_id: aircraftId,
              flight_status: 'PENDING',
              departure_airport: f.departure_airport,
              arrival_airport: f.arrival_airport,
              departure_date: f.departure_date,
              arrival_date: null,
            }))
          );
        }
      } catch (flightsErr) {
        console.error('Failed to create flight records after capture:', flightsErr);
      }

      // invalidates quotes
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      // Send payment confirmation email to client
      if (quote?.client?.email) {
        try {

          const sales = {
            payment_method: "Wire",
            withOutSales: true
          };

          const emailHtml = await buildQuotePaymentSecuredBookingEmailHtml({
            quote,
            selectedBidOption: bidOption,
            client: quote?.client,
            sales,
            organization
          });
          
          apiClient.sendMessage({
            From: 'tools@trustjet.ai',
            To: quote.client?.email,
            ReplyTo: brokerProfile?.email || '',
            Subject: `TrustJet – Booking Secured (${quote?.quote_number})`,
            HtmlBody: emailHtml
          })
            .then(() => console.log('Payment confirmation email sent to', quote.client.email))
            .catch((err) => console.error('Failed to send payment confirmation email:', err));
        } catch (emailErr) {
          console.error('Failed to build payment confirmation email:', emailErr);
        }
      }

      // show success toast message
      toast.success('Trip confirmed and card hold has been released.');

      // close drawer
      onOpenChange?.(false);
    } catch (e) {
      console.error('Failed to confirm without capture:', e);
      toast.error('Failed to confirm trip');
    } finally {
      setAuthActionLoading(null);
    }
  };

  const handleRejectAndRelease = async () => {

    if (authActionLoading || !quoteId) return;

    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Reject this trip?',
      html: 'This will <strong>cancel the trip</strong> and <strong>release the authorized payment hold</strong> on the client\'s card. This can\'t be undone.',
      showCancelButton: true,
      confirmButtonText: 'Yes, reject trip',
      cancelButtonText: 'Keep trip',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true,
      // The drawer runs Radix Dialog in modal mode, which raises a stacking
      // context AND disables pointer events on siblings — so by default the
      // Swal renders behind the drawer and can't be clicked. Force it above
      // and re-enable pointer events on its container.
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) {
          container.style.setProperty('z-index', '9999', 'important');
          container.style.setProperty('pointer-events', 'auto', 'important');
        }
      },
    });
    if (!isConfirmed) return;

    setAuthActionLoading('reject');

    try {

      // update quote to cancelled and payment to rejected
      await apiClient.updateQuote(quoteId, { status: 'CANCELLED', payment_status: 'CANCELLED' });

      // Reject-and-release rolls the tail back FROM 'ACCEPTED' (client
      // paid) to 'SENT' (still attached to the quote, no longer the
      // chosen winner) — the quote itself is CANCELLED above, so the
      // option isn't in play any more, but the tail wasn't removed
      // from the quote here (broker didn't unshortlist it). 'DRAFT'
      // was the legacy pre-SENT-attach value and doesn't fit the new
      // lifecycle scheme.
      await apiClient.updateBidOption(bidOption.bid_option_id, { status: 'SENT' });

      if (quote?.client?.email) {
        try {
          const emailHtml = await buildQuotePaymentHoldEmailHtml({
            quote,
            selectedBidOption: bidOption,
            client: quote?.client,
            card: quote?.payments?.related_transactions?.pre_authorization?.cardAccount,
            organization
          });
          await apiClient.sendMessage({
            From: 'tools@trustjet.ai',
            To: quote?.client?.email,
            ReplyTo: brokerProfile?.email || '',
            Subject: `TrustJet – Payment Hold Released (${quote?.quote_number})`,
            HtmlBody: emailHtml
          })
            .then(() => console.log('Payment hold release confirmation email sent to', quote.client?.email))
            .catch((err) => console.error('Failed to send payment hold release confirmation email:', err));
        } catch (emailErr) {
          console.error('Failed to build payment hold release confirmation email:', emailErr);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['quoteDetails', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Trip rejected and payment released from card.');

      // close drawer
      onOpenChange?.(false);

    } catch (e) {
      console.error('Failed to reject & release:', e);
      toast.error('Failed to reject trip and release payment');
    } finally {
      setAuthActionLoading(null);
    }
  };

  const canBookFlight = !quote?.payment_status || (quote?.payment_status && quote?.payment_status!=="CAPTURED" && quote?.payment_status!=="PRE_AUTHORIZED" && quote?.payment_status!=="WIRE_TRANSFER_IN_PROGRESS");

  const quoteStatus = quote?.status;

  const excludeStatus = ["DRAFT", "SENT", "VIEWED", "SIGNED", "EXPIRED", "DECLINED", "CANCELLED"];

  // PASSENGERS_PENDING and WAITING_FLIGHT only ever exist AFTER payment
  // succeeds server-side, so the button shows unconditionally for those.
  // ACCEPTED (aliased from PAID by quoteStatus) can reach the FE without
  // payment on data drift, so we still gate it on the resolved paymentStatus
  // being one of the in-flight / captured states. CAPTURED included so a
  // fully-paid PASSENGERS_PENDING/ACCEPTED quote doesn't hide the button.
  const showBuildTripSheet =
    (quoteStatus === 'PASSENGERS_PENDING'
    || quoteStatus === 'WAITING_FLIGHT'
    || quoteStatus === 'ACCEPTED')
        && (paymentStatus === 'WIRE_TRANSFER_IN_PROGRESS'
            || paymentStatus === 'PAYMENT_PROCESSING'
            || paymentStatus === 'ACCEPTED'
            || paymentStatus === 'CAPTURED'
            || paymentStatus === 'SETTLED_VIA_PASSPORT'
          );

  const showPreauth = excludeStatus.includes(quoteStatus) && (paymentStatus && (paymentStatus=="PRE_AUTHORIZED" || paymentStatus=="WIRE_TRANSFER_IN_PROGRESS"));

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent side="right" className="w-full max-w-md lg:max-w-2xl flex flex-col p-0" hideCloseButton>
        <DrawerHeader className="sr-only">
          <DrawerTitle>Quote Details</DrawerTitle>
        </DrawerHeader>

        {/* Header */}
        <div className="flex items-start justify-between flex-shrink-0 gap-4 pb-0 p-6">
          <div className="flex-1">
            {loading ? (
              <>
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-6 w-64" />
              </>
            ) : (
              <>
                {/* TR# (trip identifier) eyebrow — sits above the trip
                    title so brokers can reference the specific quote
                    number at a glance. Matches the TR# surfaced on the
                    Trip Quote Card. Gated on truthy quote_number so
                    DRAFT quotes without a server-assigned number don't
                    render an empty label. */}
                {quote?.quote_number && (
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 inline-flex items-center gap-1.5">
                    <RiTimeLine className="w-3.5 h-3.5" />
                    TR# {quote.quote_number}
                  </p>
                )}
                <h2 className="font-semibold text-gray-900 text-2xl pr-10">
                  Trip ({quote?.is_multi_leg && quote?.bid_items?.[0]?.bid?.leg_itinerary?.length > 1
                    ? (quote.bid_items[0].bid.leg_itinerary || []).map((leg, i, arr) => (
                        <span key={i} className="inline-flex items-center gap-1 whitespace-nowrap">
                          {leg.departure_airport}
                          <RiArrowRightLine className="w-4 h-4 text-gray-400 inline" />
                          {leg.arrival_airport}
                          {i < arr.length - 1 && <span className="text-gray-400">,&nbsp;</span>}
                        </span>
                      ))
                    : <span className="inline-flex items-center gap-1 whitespace-nowrap">{quote?.departure_airport} <RiArrowRightLine className="w-4 h-4 text-gray-400 inline" /> {quote?.arrival_airport}{quote?.is_round_trip && <><RiArrowRightLine className="w-4 h-4 text-gray-400 inline" /> {quote?.departure_airport}</>}</span>
                  })
                </h2>
                {quote?.client && 
                  <p className="text-lg text-gray-500 mt-0.5">
                  Quote for {quote.client.first_name} {quote.client.last_name}
                  </p>
                }
              </>
            )}
          </div>
          {!loading && (
            <div className="shrink-0 flex flex-col items-end gap-2 mt-12 mr-[-10px]">
              {(() => {
                // Prefer the prop when the caller pre-knows the status
                // (e.g. /quotes list already has it on the card); fall
                // back to the freshly fetched quote so callsites that
                // don't pass one (e.g. NotificationDrawer) still show
                // the real badge instead of defaulting to draft.
                const effectiveStatus = status ?? quote?.status;
                // Route through the promotion helper so PASSENGERS_PENDING
                // reads as "Trip Sheet In Progress" once the client has
                // submitted passenger details.
                const displayStatus = getEffectiveQuoteStatus(
                  quote,
                  effectiveStatus === 'PAID' ? 'ACCEPTED' : effectiveStatus,
                );
                const configStatus = getQuoteStatusConfig(displayStatus);
                const Icon = configStatus.icon;
                return (
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${configStatus.className}`}>
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {configStatus.label}
                  </span>
                );
              })()}
            </div>
          )}
        </div>

        {/* Action buttons row — anchored top-right, matches the X close button styling.
            Order: Decline (admin-only, PENDING_APPROVAL only) | Duplicate | Edit | Close. */}
        <div className="absolute right-6 top-5 flex items-center gap-2">
          {(() => {
            // Decline is admin/super_admin only, and only shown while the
            // quote is awaiting their approval. Terminal transition — JB
            // must duplicate and resubmit if they want to try again.
            const statusU = (quote?.status || '').toUpperCase();
            const canDecline =
              quote &&
              statusU === 'PENDING_APPROVAL' &&
              (normalizedRole === ROLES.ADMIN || normalizedRole === ROLES.SUPER_ADMIN);
            if (!canDecline) return null;
            return (
              <button
                type="button"
                onClick={() => setDeclineConfirmOpen(true)}
                disabled={isDeclining}
                title="Decline this quote submission"
                className="px-2.5 py-1 rounded-full border border-red-200 bg-white text-red-600 text-xs font-medium hover:bg-red-50 transition-colors focus:outline-none disabled:opacity-50"
              >
                {isDeclining ? 'Declining…' : 'Decline'}
              </button>
            );
          })()}
          {quote && !isQuoteViewerRole && (
            <button
              type="button"
              onClick={handleDuplicateQuote}
              disabled={isDuplicating}
              title={isDuplicating ? (duplicateStep || 'Duplicating…') : 'Duplicate this quote into a new DRAFT'}
              className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDuplicating ? (
                <RiLoader4Line className="h-4 w-4 animate-spin" />
              ) : (
                <RiFileCopyLine className="h-4 w-4" />
              )}
              <span className="sr-only">Duplicate</span>
            </button>
          )}
          {(() => {
            // Edit gates by role:
            //   Junior Broker: only DRAFT and PENDING_APPROVAL (their own).
            //     Post-SENT edits go through admin; the JB doesn't reopen
            //     something the client has already seen.
            //   Everyone else editable: DRAFT / PENDING_APPROVAL / SENT /
            //     VIEWED / SIGNED. Once ACCEPTED (paid) or in the flight
            //     lifecycle, the data is locked (downstream effects).
            const statusU = (quote?.status || '').toUpperCase();
            const isJuniorBroker = normalizedRole === ROLES.JUNIOR_BROKER;
            const JB_EDITABLE = new Set(['DRAFT', 'PENDING_APPROVAL']);
            const ADMIN_EDITABLE = new Set(['DRAFT', 'PENDING_APPROVAL', 'SENT', 'VIEWED', 'SIGNED']);
            const editableSet = isJuniorBroker ? JB_EDITABLE : ADMIN_EDITABLE;
            const canEdit = quote && !isQuoteViewerRole && editableSet.has(statusU);
            if (!canEdit) return null;
            return (
              <button
                type="button"
                onClick={() => navigate(`/quotes/generate?quoteid=${quote.quote_id}`)}
                title="Edit this quote"
                className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none"
              >
                <RiPencilLine className="h-4 w-4" />
                <span className="sr-only">Edit</span>
              </button>
            );
          })()}
          <DrawerClose className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none">
            <RiCloseLine className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DrawerClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-56 pt-0 p-6">
          {loading ? (
            <div className="pt-4 space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : error ? (
            <div className="m-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <RiErrorWarningLine className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900 mb-2">Failed to load quote</h4>
                  <p className="text-sm text-red-700">{error?.message || 'Failed to load quote details'}</p>
                  <button
                    onClick={loadQuoteDetails}
                    className="mt-3 pr-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <RiRefreshLine className="w-4 h-4" />
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          ) : quote ? (() => {
            const statusUpper = (quote?.status || '').toUpperCase();
            const needsPaidAction =
              (statusUpper === 'SENT' || statusUpper === 'VIEWED' || statusUpper === 'SIGNED') &&
              (quote?.payment_status || '').toUpperCase() === 'CAPTURED';
            return (
            <>
              {/* Already-paid notice — only when quote is pre-acceptance but payment captured */}
              {needsPaidAction && (
                <div className="pr-4 pt-4">
                  <div className="border border-red-200 bg-red-50 rounded-xl p-4">
                    <p className="text-sm text-red-700 mb-3">
                      This quote is already paid. Want to set the status to Paid?
                    </p>
                    <button
                      onClick={handleMarkAsPaid}
                      disabled={isMarkingPaid}
                      className="px-4 py-2 bg-primary-700 text-white text-sm font-medium rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {isMarkingPaid && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                      {isMarkingPaid ? 'Marking...' : 'Mark as Paid'}
                    </button>
                  </div>
                </div>
              )}

              {/* Payment hold expiry — 7-day pre-auth window (Visa/MC). */}
              {showPreauth && (() => {
                const msLeft = holdExpiresAt ? holdExpiresAt.getTime() - Date.now() : null;
                const daysLeft = msLeft != null ? Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000))) : null;
                const whenStr = holdExpiresAt
                  ? formatUTCToDateTime(holdExpiresAt.toISOString(), 'UTC', 'D MMM YYYY, HH:mm')
                  : null;
                return (
                  <div className="pr-4 pt-4">
                    <div className="border border-red-200 bg-red-50 rounded-xl p-4 flex items-start gap-3">
                      <RiErrorWarningLine className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-700">
                        {holdExpiresAt
                          ? (isHoldExpired
                              ? `Payment hold expired on ${whenStr}. Re-authorization required.`
                              : `Payment hold expires on ${whenStr} (in ${daysLeft} day${daysLeft === 1 ? '' : 's'}).`)
                          : 'Payment hold expiry unknown — invoice/payment date missing.'}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Trip Details Section */}
              <div className="pt-4 pb-6">
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-4">
                    <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">TRIP DETAILS</h3>

                    {/* Row 1 — Passenger Count + Trip Type (first leg pair) */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* Passenger Count */}
                      <div className="flex items-start gap-2.5">
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                          <RiTeamLine className="h-4 w-4 text-gray-600"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600 mb-1">Passengers</p>
                          <p className="text-base font-semibold text-gray-900">
                            {quote.passenger_count || 0}
                          </p>
                        </div>
                      </div>

                      {/* Trip Type — for multi-leg, span both columns on mobile only so the airport list has room */}
                      <div className={`flex items-start gap-2.5${quote.is_multi_leg && (quote.bid_items?.[0]?.bid?.leg_itinerary?.length || 0) > 1 ? ' col-span-2 md:col-span-1' : ''}`}>
                        <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                          <RiMapPinLine className="h-4 w-4 text-gray-600"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600 mb-1">
                            {quote.is_round_trip ? 'Round Trip' : quote.is_multi_leg ? 'Multi-Leg' : 'One-Way'}
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {quote.is_multi_leg && (quote.bid_items?.[0]?.bid?.leg_itinerary?.length || 0) > 1
                              ? quote.bid_items[0].bid.leg_itinerary.map((leg, i, arr) => (
                                  <span key={i} className="inline-block whitespace-nowrap">
                                    {leg.departure_airport} → {leg.arrival_airport}{i < arr.length - 1 ? ', ' : ''}
                                  </span>
                                ))
                              : <>{quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.departure_airport} → {quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.arrival_airport}</>
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Row 2 — Departure Date (full width, first leg's date) */}
                    <div className="flex items-start gap-2.5 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                        <RiCalendar2Line className="h-4 w-4 text-gray-600"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-600 mb-1">Departure Date</p>
                        <p className="text-base font-semibold text-gray-900">
                          {formatUTCToDateTime(
                            quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.departure_date,
                            getAirportTimezone(quote.bid_items?.[0]?.bid?.leg_itinerary?.[0]?.departure_airport),
                            "dddd, MMMM D, YYYY"
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Per-leg compact rows, separated by dividers */}
                    <div className="space-y-2 border-t border-gray-100 pt-2">
                      {(quote.bid_items?.[0]?.bid?.leg_itinerary || []).map((leg, i) => (
                        <div key={`ml-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {leg.departure_airport}</span>
                            <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                            <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{leg.arrival_airport}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <RiCalendarLine className="w-3.5 h-3.5" />
                              <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "MMM D")}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <RiTimeLine className="w-3.5 h-3.5" />
                              <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "HH:mm")}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Expires pill */}
                  {quote.bid_items?.[0]?.bid?.valid_until && (
                  <div className="bg-gray-100 px-4 py-1.5 flex items-center justify-center gap-1.5 mx-4 mb-4 rounded-2xl">
                    <RiCalendarScheduleLine className="w-3.5 h-3.5 text-gray-500" />
                    <p className="text-xs text-gray-600">
                      Expires on {new Date(quote.bid_items?.[0]?.bid?.valid_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>)}
                </div>
              </div>

              {/* Preferred Payment Method */}
              {(() => {
                if(paymentStatus=="WIRE_TRANSFER_IN_PROGRESS" || paymentStatus=="PRE_AUTHORIZED" || paymentStatus=="PAYMENT_PROCESSING" || paymentStatus=="CAPTURED" || paymentStatus === "SETTLED_VIA_PASSPORT")
                {
                  let isCard = false;
                  if(quote?.preferred_method)
                  {
                    isCard = quote?.preferred_method == "CREDIT_CARD";
                  }else{
                    isCard = paymentStatus != "WIRE_TRANSFER_IN_PROGRESS";
                  }
                  const label = !isCard ? 'Wire Transfer' : 'Card';
                  const Icon = !isCard ? RiBankLine : RiBankCardLine;
                  return (
                    <div className="pb-4">
                      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                        <span className="text-sm text-gray-600">{paymentStatus=="CAPTURED" || paymentStatus === "SETTLED_VIA_PASSPORT" ? 'Payment Method' : 'Preferred payment method'}</span>
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                          <Icon className="w-4 h-4 text-gray-700" />
                          {label}
                        </span>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Aircraft Options Section */}
              {quote.bid_items && quote.bid_items.length > 0 && (
                <div className="pb-6">
                  {quote.bid_items.map((bidItem, bidIndex) => {
                    // Once the client picks & pays, exactly one option flips
                    // to status=ACCEPTED (CardInfoForm.payQuote). Collapse the
                    // grid to that one so the broker sees the final aircraft
                    // instead of every option the client was offered.
                    const allOptions = bidItem.bid?.bid_options || [];
                    const acceptedOption = selectAcceptedBidOption(allOptions);
                    const optionsToRender = acceptedOption
                      ? [acceptedOption]
                      : allOptions.filter(option => option.shortlisted === true);
                    return optionsToRender.map((option, optionIndex) => {
                      // Merge per-quote overrides (quote.files.aircraft_overrides[aircraft_id])
                      // so this card renders the broker's per-quote edits without
                      // mutating the shared aircraft record.
                      const aircraft = getEffectiveAircraft(option.aircraft, quote);
                      return (
                      <div key={`${bidIndex}-${optionIndex}`} className="mb-4 border border-gray-200 rounded-2xl overflow-hidden">

                        <div className="rounded-t-2xl overflow-hidden">
                          <ImageCarousel
                            images={(() => {
                              const photos = getAircraftPhotos(aircraft, true);
                              return Array.isArray(photos) ? photos : [photos];
                            })()}
                            aircraft={aircraft}
                            alt={getAircraftModelName(aircraft)}
                            className="h-[350px]"
                          />
                        </div>

                        {/* Aircraft Details */}
                        <div className="p-4">
                          <div className="relative mb-2">
                            <div className="space-y-2">
                              <h4 className="text-xl font-semibold text-gray-900">{getAircraftModelName(aircraft)}</h4>
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <RiPlaneLine className="h-4 w-4"/>
                                 {aircraft?.tail_number}
                              </p>
                                {/* Operator — route through the per-quote
                                    override so brokers see their edited
                                    label here, not the base record. */}
                                {option.operator?.name && (
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                                    <RiBuildingLine className="h-4 w-4"/>
                                    <span>{getOperatorWithLocation(option.operator, quote, aircraft?.aircraft_id)}</span>
                                    </div>
                                )}
                            </div>
                            {!isFinanceRole && (quote?.status != 'SENT' && quote?.status != 'ACCEPTED' && quote?.status != 'IN_TRANSIT') && (option.operator?.trust_score !== null && option.operator?.trust_score !== undefined) && (
                               <div className="absolute top-0 right-0 scale-75 origin-top-right">
                              <TrustScore score={option.operator.rating_tiers} numericScore={option.operator?.trust_score ?? null} showBadge/>
                              </div>
                            )}
                          </div>

                          {/* Price */}
                          <div className="inline-flex items-center px-3 py-1.5 rounded-2xl gap-2 mb-3 bg-accent">
                            <RiPriceTag3Line className="h-4 w-4 font-bold text-[#0C3E71]" />
                            <span className="text-sm font-semibold text-[#0C3E71]">{formatPrice(option.total_price || option.final_price)}</span>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-4">
                            {option.operator?.argus_rating && option.operator?.argus_rating!="No" &&
                            <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                              <RiTrophyLine className="h-4 w-4 text-gray-600"/> {option.operator?.argus_rating}
                            </span>}
                            
                            {option.operator?.wyvern_rating && option.operator?.wyvern_rating!="No" &&
                            <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
                              <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
                            </span>
                            }

                            {aircraft.aircraft_features?.no_aog &&
                            <span className={`flex px-2 py-1 bg-green-100 text-green-700 text-xs rounded-2xl font-medium`}>
                              <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
                            </span>
                            }
                          </div>

                          {/* Per-quote broker note (from quote.files.aircraft_overrides). */}
                          {aircraft?.quote_notes && (
                            <div className="mb-3 mt-1 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                              <p className="text-xs text-amber-800 whitespace-pre-wrap">{aircraft.quote_notes}</p>
                            </div>
                          )}

                          {/* About Aircraft
                          <div>
                            <h6 className="text-sm text-gray-500 font-[500]">About the Aircraft</h6>
                            <p className="text-sm text-gray-500">{aircraft?.description || "This aircraft offers exceptional comfort and performance for your journey. With a range of 3,600 nautical miles and seating for up to 10 passengers, it's perfect for your flight."}</p>
                          </div> */}


                           <div className="rounded-xl mt-3">
                              <h6 className="text-sm text-gray-500 font-[500] mb-2">Amenities</h6>

                              {aircraft.aircraft_features?.custom_features && aircraft.aircraft_features?.custom_features?.length > 0 ? (
                                  <div className="grid grid-cols-1 gap-1">
                                    {aircraft.aircraft_features?.custom_features.map((feature, index) => (
                                      <div key={index} className="flex border-b py-1">
                                        <div className="w-10 h-7 flex items-center justify-center flex-shrink-0">
                                          <RiCheckLine className="w-5 h-5 text-gray-500" />
                                        </div>
                                        <div>
                                          <div className="text-sm text-gray-500">{feature}</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                              ): (
                                <p className="text-sm text-gray-400">No amenities included.</p>
                              )}
                            </div> 


                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-3">

                              {quote?.files?.tripsheet && (
                                <button
                                  className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                                  onClick={() => {
                                    window.open(resolveAssetUrl(quote.files.tripsheet), '_blank');
                                  }}
                                >
                                  <RiDownload2Line className="w-3.5 h-3.5" />
                                  <span>Download PDF</span>
                                </button>
                              )}

                              <button
                                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs rounded-full hover:bg-gray-50 transition-colors whitespace-nowrap"
                                onClick={() => {
                                  // Prefer the signed contract when it exists — that's the
                                  // legally-binding version the client agreed to. Fall back
                                  // to the unsigned template + org branding template as before.
                                  const contractUrl =
                                    quote?.files?.contract_signed
                                    || quote?.files?.contract
                                    || organization?.branding?.contract;
                                  if (contractUrl) {
                                    window.open(resolveAssetUrl(contractUrl), '_blank');
                                  }
                                }}
                              >
                                <RiFileLine className="w-3.5 h-3.5" />
                                <span>Review Contract</span>
                              </button>

                              {canBuildTripSheet && canBookFlight && option?.status !== 'ACCEPTED' &&
                              <button
                                className="col-span-2 lg:col-span-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-accent text-primary-500 text-xs rounded-full transition-colors whitespace-nowrap"
                                onClick={() => {
                                  setSelectedBidOptions([option]);
                                  navigate(`/checkout?quoteid=${quote.quote_id}&option=${option.bid_option_id}`);
                                }}
                                disabled={isSending}
                              >
                                <RiPlaneLine className="w-3.5 h-3.5" />
                                <span>Book This Flight</span>
                              </button>
                              }

                            </div>
                       
                        </div>
                      </div>
                      );
                    });
                  })}
                </div>
              )}

              {/* Cancel & Set Positioning - only if flight exists for this quote */}
              {/* {hasFlight && (quote?.status?.toUpperCase() === 'ACCEPTED' || quote?.status?.toUpperCase() === 'IN_TRANSIT') && (
                <div className="flex gap-3 px-3">
                  <button
                    disabled={!!flightActionLoading}
                    className="flex-1 flex items-center justify-center gap-2 pr-4 py-2.5 bg-red-50 text-red-600 text-sm rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleFlightStatusUpdate('CANCELLED')}
                  >
                    {flightActionLoading === 'cancel' ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiCloseLine className="w-4 h-4" />}
                    <span>Cancel</span>
                  </button>
                  {Array.isArray(flightData) && flightData.length > 0 && flightData.every(f => f.flight_status?.toUpperCase() === 'POSITIONING') ? (
                    <span className="flex-1 flex items-center justify-center gap-2 pr-4 py-2.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-xl">
                      <RiMapPinLine className="w-4 h-4" />
                      <span>Positioning</span>
                    </span>
                  ) : (
                    <button
                      disabled={!!flightActionLoading}
                      className="flex-1 flex items-center justify-center gap-2 pr-4 py-2.5 bg-primary-500 text-white text-sm rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleFlightStatusUpdate('POSITIONING')}
                    >
                      {flightActionLoading === 'positioning' ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiMapPinLine className="w-4 h-4" />}
                      <span>Set Positioning</span>
                    </button>
                  )}
                </div>
              )} */}

              {/* Flight Picker Popup */}
              {canBuildTripSheet && showFlightPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
                    <div className="flex items-center justify-between pr-4 py-3 border-b">
                      <h3 className="text-sm font-semibold text-gray-900">Select a flight for <b>{flightData[0]?.aircraft?.tail_number || flightData[0]?.tail_number || 'N/A'}</b></h3>
                      <button onClick={() => setShowFlightPicker(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                        <RiCloseLine className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y">
                      {(Array.isArray(flightData) ? flightData : []).map((flight) => (
                        <div key={flight.flight_id} className="flex items-center justify-between pr-4 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-500 truncate">
                              <b>{flight.departure_airport || '—'}</b> → <b>{flight.arrival_airport || '—'}</b>
                            </p>
                          </div>
                          {flight?.flight_status?.toUpperCase() === 'POSITIONING' ? (
                            <span className="ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-lg">
                              <RiMapPinLine className="w-3.5 h-3.5" />
                              Positioning
                            </span>
                          ) : (
                            <button
                              disabled={flightActionLoading === `positioning-${flight.flight_id}`}
                              className="ml-3 flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-white text-xs rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleFlightStatusUpdate('POSITIONING', flight.flight_id)}
                            >
                              {flightActionLoading === `positioning-${flight.flight_id}` ? (
                                <RiLoader4Line className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RiMapPinLine className="w-3.5 h-3.5" />
                              )}
                              <span>Set Positioning</span>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {quote.bid_items?.[0]?.bid?.valid_until && (
                <div className="d-block pr-4">
                  <p className="text-sm bg-gray-100 text-gray-900 px-5 py-4 rounded-full">
                    This quote is valid until {formatUTCToDateTime(quote.bid_items?.[0]?.bid?.valid_until, 'UTC', 'dddd, MMMM D, YYYY')}. Prices are subject to availability and may change.
                  </p>
                </div>
              )}

              {/* Audit rows — Admin-only. `creator` / `sender` join arrives via
                  the include=creator,sender on getQuoteById; if either side is
                  the currently signed-in user, reuse userProfile so we don't
                  redisplay a stale name when the user updates their own
                  profile mid-session. */}
              {isAdminRole && (
                <div className="pr-4 mt-3 space-y-2 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Created by</span>
                    <span className="text-sm text-gray-900 font-medium">
                      {formatUserFullName(
                        quote?.created_by === userProfile?.userprofile_id
                          ? userProfile
                          : (quote?.creator || brokerProfile),
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Sent to Client By</span>
                    <span className="text-sm text-gray-900 font-medium">
                      {formatUserFullName(
                        quote?.sender_id === userProfile?.userprofile_id
                          ? userProfile
                          : quote?.sender,
                      )}
                    </span>
                  </div>
                </div>
              )}
            </>
            );
          })() : null}
        </div>

        {/* Floating footer — only shown when the quote is at a stage where a trip sheet makes sense.
            The mobile bottom nav (z-index 1000, fixed bottom) overlays the drawer (z-50), so we lift
            the footer above it on mobile. The nav is hidden at md+, so no spacer needed there. */}
        {showPreauth && (
          <div className="absolute inset-x-0 bottom-[72px] md:bottom-0 z-10 border-t border-gray-200 bg-white/70 backdrop-blur-md px-4 py-3 space-y-2">
            <button
              type="button"
              onClick={openConfirmAndCaptureFlow}
              disabled={!!authActionLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-700 text-sm font-medium rounded-full border border-transparent hover:bg-white hover:border-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {authActionLoading === 'confirmCapture' && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              <span>Confirm trip and take payment from card</span>
            </button>
            {quote?.payment_status === 'WIRE_TRANSFER_IN_PROGRESS' && (
            <button
              type="button"
              onClick={handleConfirmWithoutCapture}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-700 text-sm font-medium rounded-full border border-transparent hover:bg-white hover:border-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {authActionLoading === 'confirmOnly' && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              <span>Confirm trip without taking payment from card</span>
            </button>)}
            <button
              type="button"
              onClick={handleRejectAndRelease}
              disabled={!!authActionLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 text-sm font-medium rounded-full border border-transparent hover:bg-white hover:border-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {authActionLoading === 'reject' && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              <span>Reject trip and release payment from card</span>
            </button>
          </div>
        )}

        {canBuildTripSheet && quote && showBuildTripSheet && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 mb-[72px] md:mb-0">
            <button
              type="button"
            onClick={() => navigate(`/trip-sheet?quoteid=${quote.quote_id}`)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium rounded-full transition-colors"
            >
              <RiFileLine className="w-4 h-4" />
              <span>Build Trip Sheet</span>
            </button>
          </div>
        )}
      </DrawerContent>

      {/* Themed dialogs replacing window.confirm for the duplicate flow */}
      <Modal
        isOpen={duplicateConfirmOpen}
        onClose={() => !isDuplicating && setDuplicateConfirmOpen(false)}
        title="Duplicate this quote?"
        size="sm"
        closeOnOutsideClick={!isDuplicating}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
              {isDuplicating
                ? <RiLoader4Line className="w-5 h-5 text-primary-600 animate-spin" />
                : <RiFileCopyLine className="w-5 h-5 text-primary-600" />}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 leading-relaxed">
                A new <span className="font-semibold">DRAFT</span> will be created with the same
                trip details — pricing and passengers carry over so you can edit before sending.
              </p>
              {isDuplicating && (
                <p className="mt-2 text-xs text-primary-700 font-medium" aria-live="polite">
                  {duplicateStep || 'Duplicating…'}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setDuplicateConfirmOpen(false)}
              disabled={isDuplicating}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={runDuplicate}
              disabled={isDuplicating}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {isDuplicating && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              {isDuplicating ? (duplicateStep || 'Duplicating…') : 'Duplicate'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={editPromptOpen}
        onClose={dismissEditDuplicate}
        title="Edit the new duplicate?"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <RiCheckLine className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Duplicate created{duplicatedQuote?.quote_number ? (
                <> — <span className="font-semibold">{duplicatedQuote.quote_number}</span></>
              ) : null}.{' '}
              Do you want to edit it now?
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={dismissEditDuplicate}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={acceptEditDuplicate}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800 inline-flex items-center gap-2"
            >
              <RiPencilLine className="w-4 h-4" />
              Edit now
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment Settlement Notice — one-time informational gate before
          card capture. The component owns the checkbox state and the
          localStorage write; the drawer just runs the actual capture. */}
      {/* Card capture error details — surfaced from the "Learn more" link
          on the toast that fires when saleCompletionMxMerchantPayment
          fails. Keeps the raw provider error out of the toast itself. */}
      <Modal
        isOpen={captureErrorOpen}
        onClose={() => setCaptureErrorOpen(false)}
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Card payment failed</h2>
              <p className="text-sm text-gray-500">
                The processor returned the following error while trying to capture the payment:
              </p>
            </div>
          </div>
          <pre className="max-h-64 overflow-auto text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap break-words font-mono">
{captureErrorDetails || 'Unknown error'}
          </pre>
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setCaptureErrorOpen(false)}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <PaymentSettlementNoticeModal
        isOpen={settlementNoticeOpen}
        onClose={() => setSettlementNoticeOpen(false)}
        onConfirm={() => {
          setSettlementNoticeOpen(false);
          handleConfirmAndCapture();
        }}
        storageKey={storageKeys.QUOTE_SETTLEMENT_NOTICE_DISMISSED}
        brokerId={userProfile?.userprofile_id}
        isLoading={authActionLoading === 'confirmCapture'}
      />

      {/* Decline confirmation — terminal transition, so gate behind a confirm. */}
      <Modal
        isOpen={declineConfirmOpen}
        onClose={() => (isDeclining ? null : setDeclineConfirmOpen(false))}
        title="Decline this quote?"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              This will mark the quote as <span className="font-semibold">Declined</span>.
              The Junior Broker will need to duplicate and resubmit if they want to try again.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setDeclineConfirmOpen(false)}
              disabled={isDeclining}
              className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDecline}
              disabled={isDeclining}
              className="px-4 py-2 text-sm text-white bg-red-600 rounded-full hover:bg-red-700 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {isDeclining && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              {isDeclining ? 'Declining…' : 'Decline'}
            </button>
          </div>
        </div>
      </Modal>
    </Drawer>
  );
};

export default QuoteDetailsDrawer;